import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { useQuery } from "@tanstack/react-query";
import AddMemberships from "./AddMemberships";
import {
  createAdminMembership,
  getMembershipListing,
} from "../../../apis/membershipApi";
import ViewMemberships from "./ViewMemberships";
import AddMembershipForm from "./AddMembershipForm";
import { toast } from "react-toastify";
import { manageMembershipsByAdmin } from "../../../queryOptions/ManageMembership";

const ManageMemberships = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0);
  const [isAddMembershipOpen, setIsAddMembershipOpen] = useState(false);
  const [membershipListing, setMembershipListing] = useState([]);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const membershipPlansQuery = useQuery({
    queryKey: ["membershipPlans"],
    queryFn: getMembershipListing,
  });
  const { data, refetch } = membershipPlansQuery;
  const { refetch: refetchMemberships } = useQuery(manageMembershipsByAdmin());

  useEffect(() => {
    if (data) {
      setMembershipListing(data);
    }
  }, [data]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setLoading(true);
    try {
      await refetch();
      await refetchMemberships();
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const handleTabChange = async (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setTabValue(newValue);

    // If switching to View Memberships tab (index 1), refresh the data
    // if (newValue === 1) {
    //   await handleRefresh();
    // }
  };

  // Handle opening the Add Booking form
  const handleBooking = () => {
    setIsAddMembershipOpen(true);
  };

  // Handle closing the Add Booking form
  const handleCloseAddMembership = () => {
    setIsAddMembershipOpen(false);
  };

  const handleAddMembership = async (membershipData: any) => {
    setLoading(true);
    try {
      const response = await createAdminMembership(membershipData);
      if (response.url) {
        window.location.href = response.url;
      }
      if (response.message === "Membership created successfully") {
        toast.success("Membership created successfully!");
      }

      await refetch();
    } catch (error: any) {
      console.error("Error creating membership:", error);
      const errorMessage = error.response.data?.message;
      if (errorMessage === "User already has an active membership") {
        toast.error("User already have an active membership.");
      } else {
        toast.error("Unexpected error during membership purchase.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ px: 1, py: 2 }}>
      <Paper>
        {notification && (
          <Alert
            severity={notification.type}
            onClose={() => setNotification(null)}
            sx={{ mb: 2 }}
          >
            {notification.message}
          </Alert>
        )}
      </Paper>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, md: 3 },
          borderRadius: 2,
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="h5" component="h1" fontWeight="bold">
            Manage Memberships
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleBooking}
            >
              Add Membership
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                refreshing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleRefresh}
              disabled={refreshing}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="membership management tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="View Memberships" />
            {localStorage.getItem("userType") === "admin" && (
              <Tab label="Membership Listing" />
            )}
            {/* <Tab label="Terms & Conditions" /> */}
          </Tabs>
        </Box>

        {/* Membership Listing Tab */}
        <Box
          role="tabpanel"
          hidden={tabValue !== 1}
          sx={{ height: "100%", width: "100%" }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <AddMemberships />
          )}
        </Box>

        {/* View Memberships Tab */}
        <Box role="tabpanel" hidden={tabValue !== 0} sx={{ width: "100%", minWidth: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>
                Refreshing membership data...
              </Typography>
            </Box>
          ) : (
            <ViewMemberships />
          )}
        </Box>

        {/* Terms & Conditions Tab */}
        {/* <Box role="tabpanel" hidden={tabValue !== 2}>
          <ManageTermsConditions />
        </Box> */}
      </Paper>

      {/* Add Booking Form Dialog */}
      <AddMembershipForm
        open={isAddMembershipOpen}
        onClose={handleCloseAddMembership}
        onAddMembership={handleAddMembership}
        membershipListing={membershipListing}
        // bookings={bookings}
      />
    </Box>
  );
};

export default ManageMemberships;
